import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatDateTimeKo, formatKRW } from '@/lib/format';

interface BookingForCancel {
  id: string;
  status: string;
  starts_at: string;
  duration_minutes: number;
  price_krw: number;
  shaman_display_name: string;
  shaman_slug: string;
}

export function CancelPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const bookingId = params.get('booking');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: booking, isLoading } = useQuery({
    queryKey: ['booking-cancel', bookingId, token],
    enabled: !!bookingId && !!token,
    queryFn: async (): Promise<BookingForCancel | null> => {
      const { data, error } = await supabase.rpc('get_booking_by_token', {
        p_booking_id: bookingId!,
        p_token: token!,
      });
      if (error) throw error;
      const rows = data as BookingForCancel[];
      return rows?.[0] ?? null;
    },
  });

  const cancel = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cancel-booking', {
        body: { booking_id: bookingId, cancel_token: token },
      });
      if (error) throw new Error(error.message ?? '취소 실패');
      const res = data as { error?: string };
      if (res?.error) throw new Error(res.error);
    },
    onSuccess: () => setDone(true),
    onError: (e: Error) => setErr(e.message),
  });

  if (!bookingId || !token) return <div className="card text-danger">잘못된 접근입니다.</div>;
  if (isLoading) return <div className="text-ink-muted text-center py-8">불러오는 중…</div>;
  if (!booking) return <div className="card text-danger">예약을 찾을 수 없습니다.</div>;

  if (done) {
    return (
      <div className="card space-y-3">
        <h1 className="text-xl font-bold">예약이 취소되었습니다.</h1>
        <p className="text-base text-ink-muted">환불은 영업일 기준 3~5일 소요됩니다.</p>
        <Link to={`/b/${booking.shaman_slug}`} className="btn-primary inline-flex">다시 예약하기</Link>
      </div>
    );
  }

  const daysUntil = Math.floor((new Date(booking.starts_at).getTime() - Date.now()) / 86_400_000);
  const canCancel = daysUntil >= 3;

  return (
    <div className="card space-y-4">
      <h1 className="text-xl font-bold">예약 취소</h1>
      <div className="rounded-lg bg-surface-alt p-4 space-y-1">
        <div className="font-semibold">{booking.shaman_display_name}</div>
        <div>{formatDateTimeKo(booking.starts_at)}</div>
        <div className="text-base text-ink-muted">{booking.duration_minutes}분 · {formatKRW(booking.price_krw)}</div>
      </div>
      {canCancel ? (
        <>
          <p className="text-base">정말 예약을 취소하시겠어요?</p>
          {err && <p className="text-danger text-base">{err}</p>}
          <button className="btn-danger w-full" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            {cancel.isPending ? '처리 중…' : '취소하기'}
          </button>
        </>
      ) : (
        <>
          <p className="text-danger text-base">
            예약 3일 전까지만 직접 취소가 가능합니다. 무당님께 직접 연락해주세요.
          </p>
        </>
      )}
      <Link to={`/booking/${bookingId}?token=${token}`} className="btn-secondary inline-flex w-full">돌아가기</Link>
    </div>
  );
}
